import React, { useEffect, useState } from "react";
import s from "./StatisticsSalesperson.module.scss";
import Card from "components/Card/Card";
import { useTranslation } from "react-i18next";
import { fetchStatistics } from "./model";
import TextMAS from "components/TextMAS/TextMAS";
import AnalyticsTable from "components/AnalyticsTable/AnalyticsTable";
import { amountConversion } from "utils/utils";
import { useAuth } from "utils/useAuth/useAuth";
import PageContainer from "components/PageContainer/PageContainer";

const PAGE_LOCALE_KEY = "page.analytics.systemStatistics.salesperson.";

const StatisticsSalesperson = ({ focus }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [error, setError] = useState();
  const [hasFetched, setHasFetched] = useState(false);

  const [dataSource, setDataSource] = useState({});

  useEffect(() => {
    if (focus && !hasFetched) {
      getStatistics();
      setHasFetched(true);
    }
  }, [focus]);

  const getStatistics = async () => {
    setIsLoading(true);
    const response = await fetchStatistics();

    if (!response) {
      setError("networkError");
      setIsLoading(false);
      return;
    }

    switch (response?.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        setIsUnauthorized(true);
        setIsLoading(false);
        return;
      }
      default: {
        setError(true);
        setIsLoading(false);
        return;
      }
      case 200:
        break;
    }

    const result = response?.data;
    switch (result?.responseCode) {
      case "0000":
        setDataSource(result.data);
        break;
      case "1200":
        setIsUnauthorized(true);
        break;
      default:
        setError(t(PAGE_LOCALE_KEY + "fetchingError.salespersonStat"));
        break;
    }
    setIsLoading(false);
  };

  const getRenderFn =
    (categoryKey) =>
    ({ data, key }) => {
      return `${amountConversion(
        data?.[categoryKey]?.["offline" + key],
      )} / ${amountConversion(data?.[categoryKey]?.["online" + key])}`;
    };
  const sortFn =
    (categoryKey) =>
    ({ data, key }) => {
      return (
        +data?.[categoryKey]?.["offline" + key] +
        +data?.[categoryKey]?.["online" + key]
      );
    };
  const salesperson = [
    {
      id: "name",
      title: t(PAGE_LOCALE_KEY + "salesperson.columnTitle.1"),
      key: "name",
      sorting: "asc",
    },
    {
      id: "merchantMonthlyNew",
      title: t(PAGE_LOCALE_KEY + "salesperson.columnTitle.2"),
      key: "MonthlyNew",
      sorting: true,
      sortingKey: sortFn("merchantCount"),
      render: getRenderFn("merchantCount"),
    },
    {
      id: "merchantYearlyNew",
      title: t(PAGE_LOCALE_KEY + "salesperson.columnTitle.3"),
      key: "YearlyNew",
      sorting: true,
      sortingKey: sortFn("merchantCount"),
      render: getRenderFn("merchantCount"),
    },
    {
      id: "merchantActive",
      title: t(PAGE_LOCALE_KEY + "salesperson.columnTitle.4"),
      key: "Active",
      sorting: true,
      sortingKey: sortFn("merchantCount"),
      render: getRenderFn("merchantCount"),
    },
    {
      id: "transactionVolumeCurrentYear",
      title: t(PAGE_LOCALE_KEY + "salesperson.columnTitle.5"),
      key: "CurrentYear",
      sorting: true,
      sortingKey: sortFn("transactionVolume"),
      render: getRenderFn("transactionVolume"),
    },
    {
      id: "transactionVolumeCurrentMonth",
      title: t(PAGE_LOCALE_KEY + "salesperson.columnTitle.6"),
      key: "CurrentMonth",
      sorting: true,
      sortingKey: sortFn("transactionVolume"),
      render: getRenderFn("transactionVolume"),
    },
    {
      id: "transactionVolumeTotal",
      title: t(PAGE_LOCALE_KEY + "salesperson.columnTitle.7"),
      key: "Total",
      sorting: true,
      sortingKey: sortFn("transactionVolume"),
      render: getRenderFn("transactionVolume"),
    },
  ];

  return (
    <PageContainer
      isLoading={isLoading}
      unauthorized={isUnauthorized}
      errorMessage={error}
    >
      <div className={s["analytics-salesperson-container"]}>
        <div className={s["card-container"]}>
          <Card>
            <div className={s["salesperson-vertical-container"]}>
              <TextMAS
                className={s["salesperson-info-card-title"]}
                text={t(PAGE_LOCALE_KEY + "heading.number.title")}
              />
              <TextMAS className={s["salesperson-info-card-amount"]}>
                {amountConversion(dataSource?.length) ?? "-"}
              </TextMAS>
            </div>
          </Card>
        </div>
        <div className={s["table-container"]}>
          <AnalyticsTable
            initSort={{
              key: "MonthlyNew",
              asc: false,
            }}
            columns={salesperson}
            data={dataSource}
            tableStyle={{ minWidth: "1150px" }}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default StatisticsSalesperson;
